import {
  CalendarOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  ExclamationCircleOutlined,
  FileAddOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Col, Empty, Layout, Modal, PageHeader, Row, Space, Spin, Table, Typography } from 'antd';
import moment from 'moment';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Risk, RiskPriority, Smat, User } from '../../models';
import { RiskDocument } from '../../models/risk-document';
import { SmatDocument } from '../../models/smat-document';
import { apiClient, apiErrorMessage, hasPermission } from '../../utils';
import { UserContext } from '../contexts';
import { RiskDetailPage } from '../risks';
import { RiskEditPage } from '../risks/risk-edit-page';
import { RiskStatus } from '../risks/risk-status';
import { SmatFileUploadPage } from './smat-file-upload-page';
import { SmatStatus } from './smat-status';

export function SmatAuditPage(props: { smatId?: string; onSaved: () => void }) {
  const { Content } = Layout;
  const { Text } = Typography;

  const { user } = useContext(UserContext);

  const [smat, setSmat] = useState<Smat>();
  const [smatLoading, setSmatLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [riskEditModalOpen, setRiskEditModalOpen] = useState(false);
  const [riskDetailModalOpen, setRiskDetailModalOpen] = useState(false);
  const [riskDetailModalRisk, setRiskDetailModalRisk] = useState<Risk>();
  const [uploadFileModalOpen, setUploadFileModalOpen] = useState(false);
  const [fileDeleting, setFileDeleting] = useState(false);
  const [documents, setDocuments] = useState<SmatDocument[]>();
  const [fileCountError, setFileCountError] = useState(false);
  const [riskCountError, setRiskCountError] = useState(false);
  const [canEdit, setCanEdit] = useState(false);

  const baseApiUrl = (process.env.REACT_APP_ISG_BASE_API_URL || window.location.protocol + '//' + window.location.host) + '/api/';

  useEffect(() => {
    if (!smat || !user) {
      return;
    }
    if (hasPermission(user, 'smats.edit')) {
      setCanEdit(true);
      return;
    }
    if (smat?.user.id === user?.id && (smat?.status === 1 || smat?.status === 4)) {
      setCanEdit(true);
      return;
    }
    setCanEdit(false);
  }, [user, smat]);

  const loadSmat = useCallback(() => {
    setSmatLoading(true);
    apiClient
      .get(`smats/${props.smatId}`)
      .then(response => {
        setSmat(response.data);
        setDocuments(response.data.documents);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setSmatLoading(false);
      });
  }, [props.smatId]);

  useEffect(() => {
    loadSmat();
  }, [loadSmat]);

  const onRefreshClick = () => {
    loadSmat();
  };

  const deleteDocument = (documentId: string) => {
    setFileDeleting(true);
    apiClient
      .delete(`smats/${smat?.id}/documents/${documentId}`)
      .then(() => {
        loadSmat();
        props.onSaved();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setFileDeleting(false);
      });
  };

  const confirmDeleteDocument = (id: string) => {
    const onOk = () => deleteDocument(id);
    Modal.confirm({
      title: 'Doküman sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Dokümanı silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: onOk,
    });
  };

  const onCompleteClick = () => {
    setFileCountError(false);
    setRiskCountError(false);
    if (!smat?.documents?.length) {
      setFileCountError(true);
      return;
    }
    if (!smat?.risks?.length) {
      setRiskCountError(true);
      return;
    }
    setCompleteLoading(true);
    apiClient
      .post(`smats/${smat?.id}/complete`, null)
      .then(() => {
        loadSmat();
        props.onSaved();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setCompleteLoading(false);
      });
  };

  const onRiskChanged = () => {
    closeRiskEditModal();
    closeRiskDetailModal();
    loadSmat();
    props.onSaved();
  };

  const openRiskEditModal = () => {
    setRiskEditModalOpen(true);
  };

  const closeRiskEditModal = () => {
    setRiskEditModalOpen(false);
  };

  const openRiskDetailModal = (risk: Risk) => {
    setRiskDetailModalRisk(risk);
    setRiskDetailModalOpen(true);
  };

  const closeRiskDetailModal = () => {
    setRiskDetailModalOpen(false);
    setRiskDetailModalRisk(undefined);
  };

  const onFileUploaded = () => {
    closeUploadFileModal();
    loadSmat();
    props.onSaved();
  };

  const openUploadFileModal = () => {
    setUploadFileModalOpen(true);
  };

  const closeUploadFileModal = () => {
    setUploadFileModalOpen(false);
  };

  const tableColumns: any[] = [
    {
      title: 'Risk no',
      dataIndex: 'riskNo',
      key: 'riskNo',
      render: (riskNo: string, risk: Risk) => (
        <Button type="link" onClick={() => openRiskDetailModal(risk)}>
          {riskNo}
        </Button>
      ),
    },
    {
      title: 'Kategori',
      dataIndex: ['riskCategory', 'name'],
      key: 'riskCategory',
    },
    {
      title: 'Görüşülen kişi sayısı',
      dataIndex: 'interviewCount',
      key: 'interviewCount',
    },
    {
      title: 'Güvensiz davranış mı?',
      dataIndex: 'unsafeAction',
      key: 'unsafeAction',
      render: (unsafeAction: boolean) => (unsafeAction ? 'Evet' : 'Hayır'),
    },
    {
      title: 'Güvensiz davranış isim',
      dataIndex: 'unsafeActionUser',
      key: 'unsafeActionUser',
      render: (unsafeActionUser: User) => unsafeActionUser?.fullNameId,
    },
    {
      title: 'Olasılık',
      dataIndex: 'beforeProbability',
      key: 'beforeProbability',
    },
    {
      title: 'Şiddet',
      dataIndex: 'beforeSeverity',
      key: 'beforeSeverity',
    },
    {
      title: 'Risk puanı',
      dataIndex: 'beforeRiskGrade',
      key: 'beforeRiskGrade',
    },
    {
      title: 'Öncelik',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: RiskPriority) =>
        priority ? <span style={{ color: '#' + priority.color }}>{priority.name}</span> : <span>-</span>,
    },
    {
      title: 'Dokümanlar',
      dataIndex: 'documents',
      key: 'documents',
      render: (documents: RiskDocument[]) => documents?.length || '-',
    },
    {
      title: 'Oluşturma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => moment(createdAt).format('DD.MM.yyyy HH:mm'),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: number, risk: Risk) => <RiskStatus risk={risk} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={smat?.smatNo}
        subTitle={<SmatStatus smat={smat} />}
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canEdit && (
              <>
                <Button key="add" onClick={openRiskEditModal} icon={<PlusOutlined />} type="primary">
                  Risk ekle
                </Button>
                <Button key="add" onClick={openUploadFileModal} icon={<FileAddOutlined />} type="primary">
                  Doküman ekle
                </Button>
              </>
            )}
          </>,
        ]}
      />
      <Content>
        <Row gutter={16}>
          <Col span={12}>
            <Space direction="vertical">
              <Text strong>
                <EnvironmentOutlined /> {smat?.area ? `${smat?.area.code} - ${smat?.area.name}` : ''}
              </Text>
              <div>
                <Text>
                  <CalendarOutlined />
                  {` ${smat?.week}. hafta`}
                </Text>
                <Text type="secondary" style={{ marginLeft: '1rem' }}>
                  ({smat?.startDate ? moment(smat?.startDate).format('DD.MM.yyyy') : null} -
                  {smat?.endDate ? moment(smat?.endDate).format('DD.MM.yyyy') : null})
                </Text>
              </div>
              <div>
                <Text style={{ display: 'inline-block', width: '8rem' }} type="secondary">
                  Alan sorumlusu
                </Text>
                <Text>{smat?.area?.responsibleUser?.fullNameId}</Text>
              </div>
              <div>
                <Text style={{ display: 'inline-block', width: '8rem' }} type="secondary">
                  Denetleyici
                </Text>
                <Text>{smat?.user?.fullNameId}</Text>
              </div>
              <div>
                <Text style={{ display: 'inline-block', width: '8rem' }} type="secondary">
                  Tamamlayan
                </Text>
                <Text>{smat?.completedBy ? smat.completedBy.fullNameId : null}</Text>
              </div>
            </Space>
          </Col>
          <Col span={12}>
            <Space direction="vertical">
              <Text>Dokümanlar</Text>
              <div>
                {documents?.length ? (
                  <Spin spinning={fileDeleting}>
                    <Space direction="vertical" style={{ marginBottom: '1rem', width: '100%' }}>
                      {documents?.map(d => (
                        <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <a href={`${baseApiUrl}smats/${smat?.id}/documents/${d.id}`}>{d.path}</a>
                          {canEdit && <DeleteOutlined style={{ marginLeft: '0.5rem' }} onClick={() => confirmDeleteDocument(d.id)} />}
                        </div>
                      ))}
                    </Space>
                  </Spin>
                ) : smat ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Doküman eklenmemiş" />
                ) : null}
              </div>
            </Space>
          </Col>
        </Row>

        <div style={{ marginTop: '3rem' }}>
          <Table
            columns={tableColumns}
            dataSource={smat?.risks}
            rowKey="id"
            loading={smatLoading}
            pagination={false}
            size="small"
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Risk eklenmemiş" /> }}
          />
          {canEdit && smat?.status !== 2 && (
            <div style={{ paddingTop: '2rem', paddingBottom: '2rem', marginTop: '2rem', textAlign: 'center' }}>
              {fileCountError && (
                <div style={{ marginBottom: '1rem' }}>
                  <Text type="danger">SMAT için en az bir doküman eklenmelidir.</Text>
                </div>
              )}
              {riskCountError && (
                <div style={{ marginBottom: '1rem' }}>
                  <Text type="danger">SMAT için en az bir risk eklenmelidir.</Text>
                </div>
              )}
              {smat?.status !== 2 && smat?.status !== 3 && (
                <Button type="primary" size="large" onClick={onCompleteClick} loading={completeLoading}>
                  Denetlemeyi tamamla
                </Button>
              )}
            </div>
          )}
        </div>
      </Content>
      <Modal title="Risk bildirimi" visible={riskEditModalOpen} footer={null} onCancel={closeRiskEditModal} destroyOnClose={true}>
        <RiskEditPage smat={smat} onSaved={onRiskChanged} />
      </Modal>
      <Modal title="Risk bildirimi" visible={riskDetailModalOpen} width={'50%'} footer={null} onCancel={closeRiskDetailModal} destroyOnClose={true}>
        <RiskDetailPage riskId={riskDetailModalRisk?.id} onSaved={onRiskChanged} />
      </Modal>
      <Modal title="Doküman ekle" visible={uploadFileModalOpen} footer={null} onCancel={closeUploadFileModal} destroyOnClose={true}>
        <SmatFileUploadPage smat={smat} onComplete={onFileUploaded} />
      </Modal>
    </>
  );
}
